import {
  parseAsArrayOf,
  parseAsBoolean,
  parseAsString,
  useQueryStates,
} from 'nuqs'
import { useEffect, useMemo } from 'react'
import { useLocation } from 'react-router-dom'

// Storage keys for different contexts
export const LIST_FILTERS_KEY = 'billing-list-filters'
export const DETAILS_FILTERS_KEY = 'billing-details-filters'

// Define all filter parsers - align with useInvoiceQuery expected parameters
const billingFilterParsers = {
  // Search query
  search: parseAsString.withDefault('').withOptions({
    clearOnDefault: true,
  }),

  documentType: parseAsString.withDefault('all').withOptions({
    clearOnDefault: true,
  }),

  // Status filters
  status: parseAsArrayOf(parseAsString, ',').withDefault(['all']).withOptions({
    clearOnDefault: true,
  }),
  paymentStatus: parseAsArrayOf(parseAsString, ',')
    .withDefault(['all'])
    .withOptions({
      clearOnDefault: true,
    }),
  origin: parseAsArrayOf(parseAsString, ',').withDefault(['all']).withOptions({
    clearOnDefault: true,
  }),

  // Date filters - using simple date format (YYYY-MM-DD)
  dateFrom: parseAsString.withOptions({
    clearOnDefault: true,
  }),
  dateTo: parseAsString.withOptions({
    clearOnDefault: true,
  }),
  dateType: parseAsString.withDefault('emision').withOptions({
    clearOnDefault: true,
  }),

  amountFrom: parseAsString.withOptions({
    clearOnDefault: true,
  }),
  amountTo: parseAsString.withOptions({
    clearOnDefault: true,
  }),

  codeCurrency: parseAsString.withDefault('CUP').withOptions({
    clearOnDefault: true,
  }),

  // Advanced filters
  clientId: parseAsString.withOptions({
    clearOnDefault: true,
  }),
  commercialId: parseAsString.withOptions({
    clearOnDefault: true,
  }),
  paymentGatewayIds: parseAsArrayOf(parseAsString, ',')
    .withDefault([])
    .withOptions({
      clearOnDefault: true,
    }),
  productsId: parseAsArrayOf(parseAsString, ',').withDefault([]).withOptions({
    clearOnDefault: true,
  }),
  areaSalesIds: parseAsArrayOf(parseAsString, ',').withDefault([]).withOptions({
    clearOnDefault: true,
  }),
  hasDiscounts: parseAsBoolean.withOptions({
    clearOnDefault: true,
  }),
  hasCommissions: parseAsBoolean.withOptions({
    clearOnDefault: true,
  }),
  houseCosted: parseAsBoolean.withOptions({
    clearOnDefault: true,
  }),
  managedByIds: parseAsArrayOf(parseAsString, ',').withDefault([]).withOptions({
    clearOnDefault: true,
  }),
  salesByIds: parseAsArrayOf(parseAsString, ',').withDefault([]).withOptions({
    clearOnDefault: true,
  }),
  isForTakeAway: parseAsBoolean.withOptions({
    clearOnDefault: true,
  }),
  pickUpInStore: parseAsBoolean.withOptions({
    clearOnDefault: true,
  }),
  modifiedPrice: parseAsBoolean.withOptions({
    clearOnDefault: true,
  }),
  shippingByIds: parseAsArrayOf(parseAsString, ',')
    .withDefault([])
    .withOptions({
      clearOnDefault: true,
    }),
  economicCycleIds: parseAsArrayOf(parseAsString, ',')
    .withDefault([])
    .withOptions({
      clearOnDefault: true,
    }),

  economicCycleDateFrom: parseAsString.withOptions({
    clearOnDefault: true,
  }),
  economicCycleDateTo: parseAsString.withOptions({
    clearOnDefault: true,
  }),

  // Shipping location filters
  deliveryFrom: parseAsString.withOptions({
    clearOnDefault: true,
  }),
  deliveryTo: parseAsString.withOptions({
    clearOnDefault: true,
  }),

  // Order dispatch status
  isPreDispatched: parseAsBoolean.withOptions({
    clearOnDefault: true,
  }),
  isDispatched: parseAsBoolean.withOptions({
    clearOnDefault: true,
  }),

  // Additional features
  hasTips: parseAsBoolean.withOptions({
    clearOnDefault: true,
  }),
  priceSystemIds: parseAsArrayOf(parseAsString, ',')
    .withDefault([])
    .withOptions({
      clearOnDefault: true,
    }),

  // Coupon filters
  coupons: parseAsArrayOf(parseAsString, ',').withDefault([]).withOptions({
    clearOnDefault: true,
  }),

  // Province and municipality for shipping - stored as strings, validated for API conversion
  provinceId: parseAsString.withOptions({
    clearOnDefault: true,
  }),
  municipalityId: parseAsString.withOptions({
    clearOnDefault: true,
  }),
}

// Helper function to determine current page context
const usePageContext = () => {
  const location = useLocation()
  const isDetailsPage =
    location.pathname.includes('/registers-beta/all/') &&
    location.pathname.split('/').length >= 4
  const isListPage = location.pathname === '/registers-beta/all'

  return { isDetailsPage, isListPage }
}

// Helper function to save filters to localStorage
const saveFiltersToStorage = (
  filters: Record<string, unknown>,
  key: string
) => {
  // Only save non-default values to reduce storage size
  const filtersToSave = Object.entries(filters).reduce(
    (acc, [key, value]) => {
      // Skip default values
      if (
        value === '' ||
        value === 'all' ||
        value === null ||
        value === undefined ||
        (Array.isArray(value) && value.length === 0) ||
        (Array.isArray(value) && value.length === 1 && value[0] === 'all') ||
        (key === 'codeCurrency' && value === 'CUP') ||
        (key === 'dateType' && value === 'emision')
      ) {
        return acc
      }
      acc[key] = value
      return acc
    },
    {} as Record<string, unknown>
  )

  localStorage.setItem(key, JSON.stringify(filtersToSave))
}

// Helper function to get filters from localStorage
const getFiltersFromStorage = (key: string): Record<string, unknown> | null => {
  try {
    const stored = localStorage.getItem(key)
    return stored ? JSON.parse(stored) : null
  } catch {
    return null
  }
}

// Helper function to safely convert string ID to number for API calls
export const safeParseIntId = (
  id: string | null | undefined
): number | null => {
  if (!id || id.trim() === '') return null
  const parsed = parseInt(id, 10)
  return isNaN(parsed) ? null : parsed
}

// Helper function to validate filter values before use
export const validateFilterValue = (
  value: unknown,
  type: 'string' | 'number' | 'boolean' | 'array'
): boolean => {
  switch (type) {
    case 'string':
      return typeof value === 'string'
    case 'number':
      return typeof value === 'number' && !isNaN(value)
    case 'boolean':
      return typeof value === 'boolean'
    case 'array':
      return Array.isArray(value)
    default:
      return false
  }
}

export function useBillingFilters() {
  const { isDetailsPage, isListPage } = usePageContext()

  // Use different storage keys based on page context
  const storageKey = isDetailsPage ? DETAILS_FILTERS_KEY : LIST_FILTERS_KEY

  const [filters, setFilters] = useQueryStates(billingFilterParsers, {
    history: 'replace', // Use replace to avoid cluttering browser history
    shallow: true, // Client-side only updates
  })

  // Load filters from localStorage on mount
  useEffect(() => {
    const savedFilters = getFiltersFromStorage(storageKey)
    if (savedFilters) {
      // Merge saved filters with current state, preserving URL parameters
      setFilters((prevFilters) => ({
        ...prevFilters,
        ...savedFilters,
      }))
    }
  }, [storageKey, setFilters])

  // Save filters to localStorage whenever they change (but only for the appropriate page)
  useEffect(() => {
    if (isListPage || isDetailsPage) {
      saveFiltersToStorage(filters, storageKey)
    }
  }, [filters, storageKey, isListPage, isDetailsPage])

  // Memoized search params calculation
  const searchParams = useMemo(() => {
    const params = new URLSearchParams()

    // Only add non-default filter values to the URL
    if (filters.search && filters.search !== '') {
      params.set('search', filters.search)
    }

    if (filters.documentType && filters.documentType !== 'all') {
      params.set('documentType', filters.documentType)
    }

    if (filters.status && !filters.status.includes('all')) {
      params.set('status', filters.status.join(','))
    }

    if (filters.paymentStatus && !filters.paymentStatus.includes('all')) {
      params.set('paymentStatus', filters.paymentStatus.join(','))
    }

    if (filters.origin && !filters.origin.includes('all')) {
      params.set('origin', filters.origin.join(','))
    }

    if (filters.dateFrom) {
      params.set('dateFrom', filters.dateFrom)
    }

    if (filters.dateTo) {
      params.set('dateTo', filters.dateTo)
    }

    if (filters.dateType && filters.dateType !== 'emision') {
      params.set('dateType', filters.dateType)
    }

    if (filters.amountFrom) {
      params.set('amountFrom', filters.amountFrom)
    }

    if (filters.amountTo) {
      params.set('amountTo', filters.amountTo)
    }

    if (filters.codeCurrency && filters.codeCurrency !== 'CUP') {
      params.set('codeCurrency', filters.codeCurrency)
    }

    // Advanced filters
    if (filters.clientId) {
      params.set('clientId', filters.clientId)
    }

    if (filters.commercialId) {
      params.set('commercialId', filters.commercialId)
    }

    if (filters.paymentGatewayIds && filters.paymentGatewayIds.length > 0) {
      params.set('paymentGatewayIds', filters.paymentGatewayIds.join(','))
    }

    if (filters.productsId && filters.productsId.length > 0) {
      params.set('productsId', filters.productsId.join(','))
    }

    if (filters.areaSalesIds && filters.areaSalesIds.length > 0) {
      params.set('areaSalesIds', filters.areaSalesIds.join(','))
    }

    // Boolean filters
    if (filters.hasDiscounts !== null) {
      params.set('hasDiscounts', filters.hasDiscounts.toString())
    }

    if (filters.hasCommissions !== null) {
      params.set('hasCommissions', filters.hasCommissions.toString())
    }

    if (filters.houseCosted !== null) {
      params.set('houseCosted', filters.houseCosted.toString())
    }

    if (filters.isForTakeAway !== null) {
      params.set('isForTakeAway', filters.isForTakeAway.toString())
    }

    if (filters.pickUpInStore !== null) {
      params.set('pickUpInStore', filters.pickUpInStore.toString())
    }

    if (filters.modifiedPrice !== null) {
      params.set('modifiedPrice', filters.modifiedPrice.toString())
    }

    if (filters.hasTips !== null) {
      params.set('hasTips', filters.hasTips.toString())
    }

    if (filters.isPreDispatched !== null) {
      params.set('isPreDispatched', filters.isPreDispatched.toString())
    }

    if (filters.isDispatched !== null) {
      params.set('isDispatched', filters.isDispatched.toString())
    }

    // Array filters
    if (filters.managedByIds && filters.managedByIds.length > 0) {
      params.set('managedByIds', filters.managedByIds.join(','))
    }

    if (filters.salesByIds && filters.salesByIds.length > 0) {
      params.set('salesByIds', filters.salesByIds.join(','))
    }

    if (filters.shippingByIds && filters.shippingByIds.length > 0) {
      params.set('shippingByIds', filters.shippingByIds.join(','))
    }

    if (filters.economicCycleIds && filters.economicCycleIds.length > 0) {
      params.set('economicCycleIds', filters.economicCycleIds.join(','))

      if (filters.economicCycleDateFrom) {
        params.set('economicCycleDateFrom', filters.economicCycleDateFrom)
      }
      if (filters.economicCycleDateTo) {
        params.set('economicCycleDateTo', filters.economicCycleDateTo)
      }
    }

    if (filters.priceSystemIds && filters.priceSystemIds.length > 0) {
      params.set('priceSystemIds', filters.priceSystemIds.join(','))
    }

    if (filters.coupons && filters.coupons.length > 0) {
      params.set('coupons', filters.coupons.join(','))
    }

    // Location filters
    if (filters.provinceId) {
      params.set('provinceId', filters.provinceId)
    }

    if (filters.municipalityId) {
      params.set('municipalityId', filters.municipalityId)
    }

    if (filters.deliveryFrom) {
      params.set('deliveryFrom', filters.deliveryFrom)
    }

    if (filters.deliveryTo) {
      params.set('deliveryTo', filters.deliveryTo)
    }

    return params.toString()
  }, [filters])

  // Function to restore list page filters when navigating back
  const restoreListFilters = () => {
    const savedListFilters = getFiltersFromStorage(LIST_FILTERS_KEY)
    if (savedListFilters) {
      setFilters(savedListFilters)
    }
  }

  // Helper functions for easier state management
  const updateSearch = (search: string) => {
    setFilters((prev) => ({ ...prev, search }))
  }

  const updateDocumentType = (documentType: string) => {
    setFilters((prev) => ({ ...prev, documentType }))
  }

  const updateOrderStatus = (statuses: string[]) => {
    setFilters((prev) => ({ ...prev, status: statuses }))
  }

  const updatePaymentStatus = (statuses: string[]) => {
    setFilters((prev) => ({ ...prev, paymentStatus: statuses }))
  }

  const updateOrderOrigin = (origins: string[]) => {
    setFilters((prev) => ({ ...prev, origin: origins }))
  }

  const updateDateRange = (dateFrom: string | null, dateTo: string | null) => {
    setFilters((prev) => ({ ...prev, dateFrom, dateTo }))
  }

  const updateDateType = (dateType: string) => {
    setFilters((prev) => ({ ...prev, dateType }))
  }

  const updateAmountRange = (
    amountFrom: string | null,
    amountTo: string | null
  ) => {
    setFilters((prev) => ({ ...prev, amountFrom, amountTo }))
  }

  const updateCodeCurrency = (codeCurrency: string) => {
    setFilters((prev) => ({ ...prev, codeCurrency }))
  }

  // Advanced filter helper functions
  const updateClientId = (clientId: string | null) => {
    setFilters((prev) => ({ ...prev, clientId }))
  }

  const updateCommercialId = (commercialId: string | null) => {
    setFilters((prev) => ({ ...prev, commercialId }))
  }

  const updatePaymentGateway = (paymentGatewayIds: string[]) => {
    setFilters((prev) => ({ ...prev, paymentGatewayIds }))
  }

  const updateProductsId = (productsId: string[]) => {
    setFilters((prev) => ({ ...prev, productsId }))
  }

  const updateAreaSalesIds = (areaSalesIds: string[]) => {
    setFilters((prev) => ({ ...prev, areaSalesIds }))
  }

  const updateBooleanFilter = (
    key: keyof typeof filters,
    value: boolean | null
  ) => {
    setFilters((prev) => ({ ...prev, [key]: value }))
  }

  const updateArrayFilter = (key: keyof typeof filters, value: string[]) => {
    setFilters((prev) => ({ ...prev, [key]: value }))
  }

  const updateStringFilter = (
    key: keyof typeof filters,
    value: string | null
  ) => {
    setFilters((prev) => ({ ...prev, [key]: value }))
  }

  const updateDeliveryLocation = (
    deliveryFrom: string | null,
    deliveryTo: string | null
  ) => {
    setFilters((prev) => ({ ...prev, deliveryFrom, deliveryTo }))
  }

  const updateProvinceAndMunicipality = (
    provinceId: string | null,
    municipalityId: string | null
  ) => {
    setFilters((prev) => ({ ...prev, provinceId, municipalityId }))
  }

  const updateCoupons = (coupons: string[]) => {
    setFilters((prev) => ({ ...prev, coupons }))
  }

  // Individual clear functions for advanced filters
  const clearClientFilter = () => {
    setFilters((prev) => ({ ...prev, clientId: null }))
  }

  const clearCommercialFilter = () => {
    setFilters((prev) => ({ ...prev, commercialId: null }))
  }

  const clearPaymentGatewayFilter = () => {
    setFilters((prev) => ({ ...prev, paymentGatewayIds: [] }))
  }

  const clearProductsFilter = () => {
    setFilters((prev) => ({ ...prev, productsId: [] }))
  }

  const clearAreaSalesFilter = () => {
    setFilters((prev) => ({ ...prev, areaSalesIds: [] }))
  }

  const clearManagedByFilter = () => {
    setFilters((prev) => ({ ...prev, managedByIds: [] }))
  }

  const clearSalesByFilter = () => {
    setFilters((prev) => ({ ...prev, salesByIds: [] }))
  }

  const clearShippingByFilter = () => {
    setFilters((prev) => ({ ...prev, shippingByIds: [] }))
  }

  const clearEconomicCyclesFilter = () => {
    setFilters((prev) => ({ ...prev, economicCycleIds: [] }))
  }

  const updateEconomicCycleDateRange = (
    economicCycleDateFrom: string | null,
    economicCycleDateTo: string | null
  ) => {
    setFilters((prev) => ({
      ...prev,
      economicCycleDateFrom,
      economicCycleDateTo,
    }))
  }

  const clearPriceSystemsFilter = () => {
    setFilters((prev) => ({ ...prev, priceSystemIds: [] }))
  }

  const clearCouponsFilter = () => {
    setFilters((prev) => ({ ...prev, coupons: [] }))
  }

  const clearLocationFilter = () => {
    setFilters((prev) => ({ ...prev, provinceId: null, municipalityId: null }))
  }

  // Remove individual items from array filters
  const removeFromArrayFilter = (
    key: keyof typeof filters,
    valueToRemove: string
  ) => {
    setFilters((prev) => {
      const currentArray = prev[key] as string[]
      const newArray = currentArray.filter((item) => item !== valueToRemove)
      return { ...prev, [key]: newArray }
    })
  }

  const clearAllFilters = () => {
    // Clear all filters in a single atomic operation to prevent step-by-step clearing
    const clearedFilters = {
      search: '',
      documentType: 'all',
      status: ['all'],
      paymentStatus: ['all'],
      origin: ['all'],
      dateFrom: null,
      dateTo: null,
      dateType: 'emision',
      amountFrom: null,
      amountTo: null,
      codeCurrency: 'CUP', // Reset to default instead of null

      // Clear advanced filters
      clientId: null,
      commercialId: null,
      paymentGatewayIds: [],
      productsId: [],
      areaSalesIds: [],
      hasDiscounts: null,
      hasCommissions: null,
      houseCosted: null,
      managedByIds: [],
      salesByIds: [],
      isForTakeAway: null,
      pickUpInStore: null,
      modifiedPrice: null,
      shippingByIds: [],
      economicCycleIds: [],
      economicCycleDateFrom: null,
      economicCycleDateTo: null,
      deliveryFrom: null,
      deliveryTo: null,
      isPreDispatched: null,
      isDispatched: null,
      hasTips: null,
      priceSystemIds: [],
      coupons: [],
      provinceId: null,
      municipalityId: null,
    }

    setFilters(clearedFilters)

    localStorage.removeItem(storageKey)
  }

  // Check if any filters are active (excluding defaults)
  const hasActiveFilters =
    filters.search !== '' ||
    (filters.documentType && filters.documentType !== 'all') ||
    !filters.status.includes('all') ||
    !filters.paymentStatus.includes('all') ||
    !filters.origin.includes('all') ||
    filters.dateFrom ||
    filters.dateTo ||
    filters.amountFrom ||
    filters.amountTo ||
    // Advanced filters
    filters.clientId ||
    filters.commercialId ||
    filters.paymentGatewayIds.length > 0 ||
    filters.productsId.length > 0 ||
    filters.areaSalesIds.length > 0 ||
    filters.hasDiscounts !== null ||
    filters.hasCommissions !== null ||
    filters.houseCosted !== null ||
    filters.managedByIds.length > 0 ||
    filters.salesByIds.length > 0 ||
    filters.isForTakeAway !== null ||
    filters.pickUpInStore !== null ||
    filters.modifiedPrice !== null ||
    filters.shippingByIds.length > 0 ||
    filters.economicCycleIds.length > 0 ||
    filters.deliveryFrom ||
    filters.deliveryTo ||
    filters.isPreDispatched !== null ||
    filters.isDispatched !== null ||
    filters.hasTips !== null ||
    filters.priceSystemIds.length > 0 ||
    filters.coupons.length > 0 ||
    filters.provinceId ||
    filters.municipalityId

  return {
    // Current filter values
    filters,

    // Memoized search params
    searchParams,

    // Page context
    isDetailsPage,
    isListPage,

    // Basic update functions
    updateSearch,
    updateDocumentType,
    updateOrderStatus,
    updatePaymentStatus,
    updateOrderOrigin,
    updateDateRange,
    updateDateType,
    updateAmountRange,
    updateCodeCurrency,
    setFilters,
    clearAllFilters,

    // Advanced filter update functions
    updateClientId,
    updateCommercialId,
    updatePaymentGateway,
    updateProductsId,
    updateAreaSalesIds,
    updateBooleanFilter,
    updateArrayFilter,
    updateStringFilter,
    updateDeliveryLocation,
    updateProvinceAndMunicipality,
    updateCoupons,
    updateEconomicCycleDateRange,

    // Individual clear functions
    clearClientFilter,
    clearCommercialFilter,
    clearPaymentGatewayFilter,
    clearProductsFilter,
    clearAreaSalesFilter,
    clearManagedByFilter,
    clearSalesByFilter,
    clearShippingByFilter,
    clearEconomicCyclesFilter,
    clearPriceSystemsFilter,
    clearCouponsFilter,
    clearLocationFilter,
    removeFromArrayFilter,

    // localStorage functions
    restoreListFilters,

    // Helper states
    hasActiveFilters,
  }
}

// Type exports for better TypeScript support
export type BillingFilters = ReturnType<typeof useBillingFilters>['filters']
export type BillingFilterKey = keyof typeof billingFilterParsers
